// src/components/FormEditor.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  TextField,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  IconButton,
  FormControlLabel,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import ModuleEditor from "./ModuleEditor"; // Ensure this component is created
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useNavigate, useParams } from "react-router-dom";
import debounce from "lodash.debounce";
import { v4 as uuidv4 } from "uuid";

const CATEGORY_COLORS = {
  "Text + Layout Fields": "#ffec80",
  "User Information": "#ffec80", // Light Green
  "Question Fields": "#ffec80", // Light Blue
  "Media Fields": "#ffec80", // Light Purple
  "Date/Time Fields": "#ffec80", // Light Orange
  "Forsite Integrations": "#ffec80", // Light Teal
};

const AVAILABLE_FIELDS = [
  {
    category: "Text + Layout Fields",
    fields: [
      { id: "header", type: "header", label: "Header" },
      { id: "banner", type: "banner", label: "Banner" },
      { id: "subheader", type: "subheader", label: "Subheader" },
      { id: "paragraph", type: "paragraph", label: "Paragraph" },
    ],
  },
  {
    category: "User Information",
    fields: [
      { id: "user_details", type: "user_details", label: "User Details" },
      { id: "email", type: "email", label: "Email" },
      { id: "signature", type: "signature", label: "Signature" },
    ],
  },
  {
    category: "Question Fields",
    fields: [
      { id: "short_answer", type: "short_answer", label: "Short Answer" },
      {
        id: "double_short_answer",
        type: "double_short_answer",
        label: "Short Answer Row",
      },
      { id: "long_answer", type: "long_answer", label: "Long Answer" },
      { id: "checkbox", type: "checkbox", label: "Checkbox" },
      {
        id: "checkbox_group",
        type: "checkbox_group",
        label: "Vertical Checklist",
      },
      {
        id: "custom_checkbox_group",
        type: "custom_checkbox_group",
        label: "Checkbox Row",
      },
      {
        id: "radio_button_group",
        type: "radio_button_group",
        label: "Radio Button Group",
      },
      { id: "table", type: "table", label: "Table" },
      {
        id: "dropdown_select",
        type: "dropdown_select",
        label: "Dropdown Select",
      },
      {
        id: "dynamic_question_group",
        type: "dynamic_question_group",
        label: "Dynamic Question Group",
      },
    ],
  },
  {
    category: "Media Fields",
    fields: [
      { id: "image", type: "image", label: "Image" },
      { id: "image_upload", type: "image_upload", label: "Image Upload" },
    ],
  },
  {
    category: "Date/Time Fields",
    fields: [
      { id: "datetime", type: "datetime", label: "Date & Time" },
      { id: "date_range", type: "date_range", label: "Start Date + End Date" },
      { id: "time_range", type: "time_range", label: "Start Time + End Time" },
    ],
  },
  {
    category: "Forsite Integrations",
    fields: [
      {
        id: "site_information_viewer",
        type: "site_information_viewer",
        label: "Site Information Viewer",
      },
      {
        id: "document_checker",
        type: "document_checker",
        label: "Document Checker",
      },
      {
        id: "hazard_register",
        type: "hazard_register",
        label: "Hazard Register",
      },
    ],
  },
];

function FormEditor() {
  const { id } = useParams(); // Get form ID from URL
  const navigate = useNavigate(); // Initialize navigate
  const [formType, setFormType] = useState("Induction");
  const [companyId, setCompanyId] = useState("");
  const [modules, setModules] = useState([]);
  const [requiredDocuments, setRequiredDocuments] = useState([]); // New state for documents
  const [formName, setFormName] = useState(""); // State to hold the form name
  const [confirmedCompanyId, setConfirmedCompanyId] = useState("");
  const [isAnyModuleRequiresApproval, setIsAnyModuleRequiresApproval] =
    useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [availableQuestionnaires, setAvailableQuestionnaires] = useState([]); // New state for fetched questionnaires
  const [loadingCompanyId, setLoadingCompanyId] = useState(false);
  const [companyIdError, setCompanyIdError] = useState("");
  const [companyIdSuccess, setCompanyIdSuccess] = useState(false);
  const [approvalRequired, setApprovalRequired] = useState(false); // Overall form approval requirement
  const [loadingForm, setLoadingForm] = useState(true);
  const [formLoadError, setFormLoadError] = useState("");
  const [addModuleDialogOpen, setAddModuleDialogOpen] = useState(false);
  const [selectedModuleType, setSelectedModuleType] = useState("");

  const [styles, setStyles] = useState({
    buttonColor: "#1976d2",
    bannerColor: "#f5f5f5",
    header: {
      fontSize: "h5",
      fontFamily: "Roboto",
      color: "#000000",
    },
    subheader: {
      fontSize: "h6",
      fontFamily: "Roboto",
      color: "#000000",
    },
  });

  const debouncedHandleSearchChange = useMemo(
    () => debounce((e) => setSearchTerm(e.target.value), 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedHandleSearchChange.cancel();
    };
  }, [debouncedHandleSearchChange]);

  useEffect(() => {
    const anyApprovalRequired = modules.some((mod) => mod.approvalRequired);
    setIsAnyModuleRequiresApproval(anyApprovalRequired);
  }, [modules]);

  const filteredCategories = AVAILABLE_FIELDS.map((category) => ({
    ...category,
    fields: category.fields.filter((field) =>
      field.label.toLowerCase().includes(searchTerm.toLowerCase()),
    ),
  })).filter((category) => category.fields.length > 0);

  // Function to open the Add Module dialog
  const handleAddNewModule = () => {
    setAddModuleDialogOpen(true);
  };

  // Function to close the Add Module dialog
  const handleCloseAddModuleDialog = () => {
    setAddModuleDialogOpen(false);
    setSelectedModuleType("");
  };

  // Function to confirm and add the selected module
  const handleConfirmAddModule = () => {
    if (!selectedModuleType) {
      // Optionally, you can add error handling here
      return;
    }

    const newModule = createModule(selectedModuleType);
    setModules([...modules, newModule]);

    setAddModuleDialogOpen(false);
    setSelectedModuleType("");
  };

  const handleConfirmCompanyId = () => {
    if (!companyId.trim()) {
      setCompanyIdError("Company ID cannot be empty.");
      setCompanyIdSuccess(false);
      return;
    }

    // Set the confirmed Company ID without fetching questionnaires
    setConfirmedCompanyId(Number(companyId));
    setCompanyIdSuccess(true);

    // Optionally, fetch questionnaires based on companyId
    // fetchQuestionnaires(Number(companyId));
  };

  // Handle drag end event
  const onDragEnd = (result) => {
    const { source, destination, draggableId, type } = result;

    // Dropped outside any droppable area
    if (!destination) return;

    // If dragging from available fields to form canvas
    if (type === "module") {
      if (
        source.droppableId === "availableFields" &&
        destination.droppableId === "formCanvas"
      ) {
        const field = AVAILABLE_FIELDS.flatMap(
          (category) => category.fields,
        ).find((field) => field.id === draggableId);

        if (!field) {
          console.error(`Field with id "${draggableId}" not found.`);
          return;
        }
        const newModule = createModule(field.type);
        const newModules = Array.from(modules);
        newModules.splice(destination.index, 0, newModule);
        setModules(newModules);
        return;
      }

      // If reordering within the form canvas
      if (
        source.droppableId === "formCanvas" &&
        destination.droppableId === "formCanvas"
      ) {
        const reorderedModules = Array.from(modules);
        const [removed] = reorderedModules.splice(source.index, 1);
        reorderedModules.splice(destination.index, 0, removed);
        setModules(reorderedModules);
        return;
      }
    }

    // Handling dragging within nested Droppables (e.g., within a banner)
    if (type === "childModule") {
      const sourceBannerId = source.droppableId;
      const destBannerId = destination.droppableId;

      if (sourceBannerId === destBannerId) {
        // Reorder within the same banner
        const banner = modules.find((mod) => mod.id === sourceBannerId);
        if (!banner) return;
        const newChildren = Array.from(banner.children);
        const [movedModule] = newChildren.splice(source.index, 1);
        newChildren.splice(destination.index, 0, movedModule);

        const updatedBanner = { ...banner, children: newChildren };
        const newModules = modules.map((mod) =>
          mod.id === updatedBanner.id ? updatedBanner : mod,
        );
        setModules(newModules);
      } else {
        // Move between different banners
        const sourceBanner = modules.find((mod) => mod.id === sourceBannerId);
        const destBanner = modules.find((mod) => mod.id === destBannerId);

        if (!sourceBanner || !destBanner) return;

        const sourceChildren = Array.from(sourceBanner.children);
        const [movedModule] = sourceChildren.splice(source.index, 1);
        const destChildren = Array.from(destBanner.children);
        destChildren.splice(destination.index, 0, movedModule);

        const updatedSourceBanner = {
          ...sourceBanner,
          children: sourceChildren,
        };
        const updatedDestBanner = { ...destBanner, children: destChildren };

        const newModules = modules.map((mod) => {
          if (mod.id === updatedSourceBanner.id) return updatedSourceBanner;
          if (mod.id === updatedDestBanner.id) return updatedDestBanner;
          return mod;
        });
        setModules(newModules);
      }
    }
  };

  // Function to fetch the existing form data based on the form ID
  useEffect(() => {
    const fetchForm = async () => {
      try {
        const response = await axios.get(
          `https://formbuilderapi.getforsite.com/api/forms/${id}`,
        );
        const formData = response.data.formStructure;

        setFormType(formData.formType || "Induction");
        setCompanyId(formData.company_id || "");
        setModules(formData.modules || []);
        setRequiredDocuments(formData.requiredDocuments || []);
        setFormName(formData.name || "");
        setApprovalRequired(formData.approvalRequired || false);
        setStyles(
          formData.styles || {
            buttonColor: "#1976d2",
            bannerColor: "#f5f5f5",
            header: {
              fontSize: "h5",
              fontFamily: "Roboto",
              color: "#000000",
            },
            subheader: {
              fontSize: "h6",
              fontFamily: "Roboto",
              color: "#000000",
            },
          },
        );
        setLoadingForm(false);
      } catch (error) {
        console.error("Error fetching form:", error);
        setFormLoadError("Failed to load the form. Please try again later.");
        setLoadingForm(false);
      }
    };

    fetchForm();
  }, [id]);

  // Function to create a new module based on type
  const createModule = (type) => {
    const id = uuidv4(); // Generate unique ID using uuid
    const baseModule = {
      id,
      type,
      required: false,
      approvalRequired: false,
      visibilityCondition: null, // Initialize visibilityCondition to null
    };
    switch (type) {
      case "checkbox_group":
        return {
          ...baseModule,
          label: "Checkbox Group",
          options: ["Option 1", "Option 2"],
          correctResponses: [],
        };

      case "banner":
        return {
          id,
          type: "banner",
          label: "Banner",
          color: styles.bannerColor || "#f5f5f5", // Apply selected banner color or default
          children: [], // Initialize with no child modules
          required: false,
          approvalRequired: false,
        };
      case "table":
        return {
          ...baseModule,
          columns: ["Column 1", "Column 2"], // Default two columns
        };
      case "radio_button_group":
        return {
          ...baseModule,
          label: "Radio Button Group",
          options: ["Option 1", "Option 2"],
        };
      case "header":
      case "subheader":
        return {
          ...baseModule,
          text: type === "header" ? "Header Text" : "Subheader Text",
        };
      case "hazard_register":
        return {
          ...baseModule,
          label: "Hazard Register",
        };
      case "image_upload":
        return {
          ...baseModule,
          label: "Image Upload",
        };
      case "dropdown_select":
        return {
          ...baseModule,
          label: "Dropdown Label", // Default label
          options: [{ label: "Option 1", value: "option_1" }], // Default options
          multiple: false, // Allow multiple selections or not
        };
      case "email":
        return {
          id,
          type,
          label: "Email Address",
          required: true,
          approvalRequired: false,
        };
      case "site_information_viewer":
        return {
          ...baseModule,
        };
      case "image":
        return {
          ...baseModule,
          url: "https://via.placeholder.com/150",
          alt_text: "Placeholder Image",
        };
      case "paragraph":
        return {
          ...baseModule,
          content: "This is a paragraph.",
        };
      case "short_answer":
      case "long_answer":
        return {
          ...baseModule,
          label: `${type === "short_answer" ? "Short" : "Long"} Answer`,
          content: "",
        };
      case "checkbox":
        return {
          ...baseModule,
          label: "Checkbox",
        };
      case "datetime":
        return {
          ...baseModule,
          label: "Date & Time",
        };
      case "time_range":
        return {
          ...baseModule,
          label: "Time Range",
        };
      case "custom_checkbox_group":
        return {
          id,
          type: "custom_checkbox_group",
          label: "Your Question Here", // Default question label
          options: ["Yes", "No", "N/A"], // Default options
          required: false,
          approvalRequired: false,
        };
      case "dynamic_question_group":
        return {
          ...baseModule,
          label: "Dynamic Question Group",
          questions: [], // Initialize with an empty array
          randomise: 0, // Initialize with a default value
        };
      case "date_range":
        return {
          ...baseModule,
          label: "Date Range",
        };
      case "signature":
        return {
          ...baseModule,
          label: "Signature",
        };
      case "document_checker":
        return {
          ...baseModule,
          selectedQuestionnaireId: "",
        };
      case "double_short_answer":
        return {
          ...baseModule,
          label: "Double Short Answer",
          answers: ["", ""],
        };
      case "user_details":
        return {
          ...baseModule,
          userDetails: {
            firstName: "",
            lastName: "",
            phoneNumber: "",
            company: "",
            email: "",
          },
        };
      default:
        return baseModule;
    }
  };

  // Function to add a new document
  const addDocument = () => {
    const newDoc = { id: uuidv4(), name: "", required: false };
    setRequiredDocuments([...requiredDocuments, newDoc]);
  };

  // Function to update a document's details
  const updateDocument = (id, field, value) => {
    const updatedDocs = requiredDocuments.map((doc) =>
      doc.id === id ? { ...doc, [field]: value } : doc,
    );
    setRequiredDocuments(updatedDocs);
  };

  // Function to remove a document
  const removeDocument = (id) => {
    const updatedDocs = requiredDocuments.filter((doc) => doc.id !== id);
    setRequiredDocuments(updatedDocs);
  };

  // Handle module change from ModuleEditor
  const handleModuleChange = (index, updatedModule) => {
    const newModules = [...modules];
    newModules[index] = updatedModule;
    setModules(newModules);
  };

  // **Simplified updateModule function to update by module ID**
  const updateModule = (updatedModule) => {
    setModules((prevModules) =>
      prevModules.map((mod) =>
        mod.id === updatedModule.id ? updatedModule : mod,
      ),
    );
  };

  const saveForm = async () => {
    const formStructure = {
      title: `${formType} Form`,
      formType: formType,
      type: formName,
      company_id: companyId,
      modules: modules.map((module) => {
        if (module.type === "dynamic_question_group") {
          return {
            ...module,
            questions: module.questions.map((q) => ({
              ...q,
              id: q.id || uuidv4(),
            })),
          };
        }
        return module;
      }),
      requiredDocuments,
      approvalRequired: isAnyModuleRequiresApproval,
      styles,
    };

    try {
      const response = await axios.put(
        `https://formbuilderapi.getforsite.com/api/forms/${id}`,
        {
          // Corrected from formId to id
          form_structure: formStructure,
          form_type: formType,
          company_id: companyId,
          name: formName,
        },
      ); //https://formbuilderapi.getforsite.com //https://formbuilderapi.getforsite.com
      const { questionnaireId, formUrl } = response.data;
      alert(
        `Form updated successfully!\nForm ID: ${questionnaireId}\nForm URL: ${formUrl}`,
      );
      // Optionally, navigate back to the EditFormPage or elsewhere
      navigate("/edit-form");
    } catch (error) {
      console.error("Error saving form:", error);
      alert("An error occurred while saving the form.");
    }
  };

  if (loadingForm) {
    return (
      <Container maxWidth="md" sx={{ marginTop: 8, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ marginTop: 2 }}>
          Loading form...
        </Typography>
      </Container>
    );
  }

  if (formLoadError) {
    return (
      <Container maxWidth="md" sx={{ marginTop: 8 }}>
        <Alert severity="error">{formLoadError}</Alert>
      </Container>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Grid container spacing={2} sx={{ height: "100vh", padding: 2 }}>
        {/* Left Column: Available Fields */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={0}
            sx={{
              padding: 2,
              height: { xs: "auto", md: "100vh" },
              overflowY: "auto",
              position: { xs: "static", md: "sticky" },
              top: { md: 0 },
            }}
          >
            <Typography variant="h6" gutterBottom>
              Available Fields
            </Typography>
            <TextField
              placeholder="Search Fields"
              onChange={debouncedHandleSearchChange}
              value={searchTerm}
              fullWidth
              variant="outlined"
              size="small"
              sx={{ marginBottom: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {searchTerm && (
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm("")}
                        aria-label="clear search"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <Droppable
              droppableId="availableFields"
              type="module"
              isDropDisabled={true}
            >
              {(provided) => (
                <Box ref={provided.innerRef} {...provided.droppableProps}>
                  {filteredCategories.map((category, catIndex) => (
                    <Accordion key={catIndex} disableGutters>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel-content-${catIndex}`}
                        id={`panel-header-${catIndex}`}
                        sx={{
                          backgroundColor:
                            CATEGORY_COLORS[category.category] || "#e0e0e0",
                          borderRadius: 0,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold" }}
                        >
                          {category.category}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List>
                          {category.fields.map((field, index) => (
                            <Draggable
                              key={field.id}
                              draggableId={field.id}
                              index={index}
                            >
                              {(provided) => (
                                <ListItem
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  button
                                  sx={{ cursor: "grab" }}
                                >
                                  <ListItemText primary={field.label} />
                                </ListItem>
                              )}
                            </Draggable>
                          ))}
                        </List>
                        {provided.placeholder}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </Paper>
        </Grid>

        {/* Center Column: Form Canvas */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{ padding: 2, height: "100%", overflowY: "auto" }}
          >
            <Typography variant="h6" gutterBottom>
              Form Canvas
            </Typography>
            <Droppable droppableId="formCanvas" type="module">
              {(provided) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    minHeight: "80vh",
                    border: "2px dashed #ccc",
                    padding: 2,
                    borderRadius: 1,
                    backgroundColor: "#fafafa",
                  }}
                >
                  {modules.map((module, index) => (
                    <Draggable
                      key={module.id}
                      draggableId={module.id}
                      index={index}
                    >
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{ marginBottom: 2 }}
                        >
                          <ModuleEditor
                            module={module}
                            onChange={(updatedModule) =>
                              handleModuleChange(index, updatedModule)
                            }
                            onDelete={() => {
                              const newModules = [...modules];
                              newModules.splice(index, 1);
                              setModules(newModules);
                            }}
                            isEditable={true}
                            availableQuestionnaires={availableQuestionnaires}
                            confirmedCompanyId={confirmedCompanyId}
                            setAvailableQuestionnaires={
                              setAvailableQuestionnaires
                            }
                            modules={modules}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
            {/* Optionally, add a button to add new modules */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleAddNewModule()}
              sx={{ marginTop: 2 }}
            >
              Add Module
            </Button>
          </Paper>
        </Grid>

        <Dialog open={addModuleDialogOpen} onClose={handleCloseAddModuleDialog}>
          <DialogTitle>Select Module Type</DialogTitle>
          <DialogContent>
            <FormControl fullWidth>
              <InputLabel id="select-module-type-label">Module Type</InputLabel>
              <Select
                labelId="select-module-type-label"
                value={selectedModuleType}
                label="Module Type"
                onChange={(e) => setSelectedModuleType(e.target.value)}
              >
                {AVAILABLE_FIELDS.flatMap((category) => category.fields).map(
                  (field) => (
                    <MenuItem key={field.id} value={field.type}>
                      {field.label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddModuleDialog}>Cancel</Button>
            <Button
              onClick={handleConfirmAddModule}
              disabled={!selectedModuleType}
              variant="contained"
              color="primary"
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>

        {/* Right Column: Settings */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={3}
            sx={{
              padding: 2,
              height: "100vh",
              overflowY: "auto",
              position: "sticky",
              top: 0,
            }}
          >
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="h6" gutterBottom>
                Settings
              </Typography>
            </Box>

            {/* Company ID Section */}
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="company-id-content"
                id="company-id-header"
              >
                <Typography variant="subtitle1">Company ID</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    marginBottom: 2,
                  }}
                >
                  <TextField
                    label="Company ID"
                    value={companyId}
                    onChange={(e) => setCompanyId(e.target.value)}
                    variant="outlined"
                    fullWidth
                    disabled={loadingCompanyId}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirmCompanyId}
                    disabled={
                      loadingCompanyId || companyId === confirmedCompanyId
                    }
                    sx={{
                      color: "black",
                      backgroundColor: "#ffd902",
                      borderRadius: "20px",
                    }}
                  >
                    {loadingCompanyId ? "Confirming..." : "Confirm"}
                  </Button>
                </Box>

                {/* Feedback Messages */}
                {companyIdError && (
                  <Typography variant="body2" color="error" gutterBottom>
                    {companyIdError}
                  </Typography>
                )}
                {companyIdSuccess && (
                  <Typography variant="body2" color="success.main" gutterBottom>
                    Company ID confirmed successfully!
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Form Name and Type Section */}
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="form-details-content"
                id="form-details-header"
              >
                <Typography variant="subtitle1">Form Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  label="Form Name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  fullWidth
                  margin="normal"
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Select Form Type</InputLabel>
                  <Select
                    value={formType}
                    onChange={(e) => setFormType(e.target.value)}
                    label="Select Form Type"
                  >
                    <MenuItem value="Induction">Induction</MenuItem>
                    <MenuItem value="Permit">Permit</MenuItem>
                    <MenuItem value="Document">Document</MenuItem>
                  </Select>
                </FormControl>
              </AccordionDetails>
            </Accordion>

            {/* Style Customization Section */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="style-customization-content"
                id="style-customization-header"
              >
                <Typography variant="subtitle1">Style Customization</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Button Color Picker */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ marginRight: 2 }}>
                    Button Color:
                  </Typography>
                  <TextField
                    type="color"
                    value={styles.buttonColor}
                    onChange={(e) =>
                      setStyles({ ...styles, buttonColor: e.target.value })
                    }
                    variant="outlined"
                    sx={{ width: 60, height: 60, padding: 0 }}
                    inputProps={{
                      style: {
                        padding: 0,
                        border: "none",
                      },
                    }}
                  />
                </Box>

                {/* Banner Color Picker */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ marginRight: 2 }}>
                    Banner Color:
                  </Typography>
                  <TextField
                    type="color"
                    value={styles.bannerColor}
                    onChange={(e) =>
                      setStyles({ ...styles, bannerColor: e.target.value })
                    }
                    variant="outlined"
                    sx={{ width: 60, height: 60, padding: 0 }}
                    inputProps={{
                      style: {
                        padding: 0,
                        border: "none",
                      },
                    }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Header Style Customization Section */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="header-style-content"
                id="header-style-header"
              >
                <Typography variant="subtitle1">Header Style</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Font Size */}
                <FormControl fullWidth margin="normal">
                  <InputLabel>Header Font Size</InputLabel>
                  <Select
                    value={styles.header.fontSize}
                    onChange={(e) =>
                      setStyles({
                        ...styles,
                        header: { ...styles.header, fontSize: e.target.value },
                      })
                    }
                    label="Header Font Size"
                  >
                    <MenuItem value="h4">H4</MenuItem>
                    <MenuItem value="h5">H5</MenuItem>
                    <MenuItem value="h6">H6</MenuItem>
                    <MenuItem value="subtitle1">Subtitle1</MenuItem>
                    <MenuItem value="subtitle2">Subtitle2</MenuItem>
                    <MenuItem value="body1">Body1</MenuItem>
                    <MenuItem value="body2">Body2</MenuItem>
                  </Select>
                </FormControl>

                {/* Font Family */}
                <TextField
                  label="Header Font Family"
                  value={styles.header.fontFamily}
                  onChange={(e) =>
                    setStyles({
                      ...styles,
                      header: { ...styles.header, fontFamily: e.target.value },
                    })
                  }
                  fullWidth
                  margin="normal"
                />

                {/* Color Picker */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ marginRight: 2 }}>
                    Header Color:
                  </Typography>
                  <TextField
                    type="color"
                    value={styles.header.color}
                    onChange={(e) =>
                      setStyles({
                        ...styles,
                        header: { ...styles.header, color: e.target.value },
                      })
                    }
                    variant="outlined"
                    sx={{ width: 60, height: 60, padding: 0 }}
                    inputProps={{
                      style: {
                        padding: 0,
                        border: "none",
                      },
                    }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Subheader Style Customization Section */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="subheader-style-content"
                id="subheader-style-header"
              >
                <Typography variant="subtitle1">Subheader Style</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Font Size */}
                <FormControl fullWidth margin="normal">
                  <InputLabel>Subheader Font Size</InputLabel>
                  <Select
                    value={styles.subheader.fontSize}
                    onChange={(e) =>
                      setStyles({
                        ...styles,
                        subheader: {
                          ...styles.subheader,
                          fontSize: e.target.value,
                        },
                      })
                    }
                    label="Subheader Font Size"
                  >
                    <MenuItem value="h4">H4</MenuItem>
                    <MenuItem value="h5">H5</MenuItem>
                    <MenuItem value="h6">H6</MenuItem>
                    <MenuItem value="subtitle1">Subtitle1</MenuItem>
                    <MenuItem value="subtitle2">Subtitle2</MenuItem>
                    <MenuItem value="body1">Body1</MenuItem>
                    <MenuItem value="body2">Body2</MenuItem>
                  </Select>
                </FormControl>

                {/* Font Family */}
                <TextField
                  label="Subheader Font Family"
                  value={styles.subheader.fontFamily}
                  onChange={(e) =>
                    setStyles({
                      ...styles,
                      subheader: {
                        ...styles.subheader,
                        fontFamily: e.target.value,
                      },
                    })
                  }
                  fullWidth
                  margin="normal"
                />

                {/* Color Picker */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ marginRight: 2 }}>
                    Subheader Color:
                  </Typography>
                  <TextField
                    type="color"
                    value={styles.subheader.color}
                    onChange={(e) =>
                      setStyles({
                        ...styles,
                        subheader: {
                          ...styles.subheader,
                          color: e.target.value,
                        },
                      })
                    }
                    variant="outlined"
                    sx={{ width: 60, height: 60, padding: 0 }}
                    inputProps={{
                      style: {
                        padding: 0,
                        border: "none",
                      },
                    }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Approval Requirement Section */}
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="approval-requirement-content"
                id="approval-requirement-header"
              >
                <Typography variant="subtitle1">
                  Approval Requirement
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Tooltip
                  title={
                    isAnyModuleRequiresApproval
                      ? "At least one module requires approval."
                      : "No modules require approval."
                  }
                >
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isAnyModuleRequiresApproval}
                          disabled // Make it read-only
                          color="primary"
                        />
                      }
                      label={
                        isAnyModuleRequiresApproval
                          ? "Approval Required"
                          : "No Approval Required"
                      }
                    />
                  </Box>
                </Tooltip>
              </AccordionDetails>
            </Accordion>

            {/* Requires Documents Section */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="requires-documents-content"
                id="requires-documents-header"
              >
                <Typography variant="subtitle1">Requires Documents</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {requiredDocuments.map((doc, index) => (
                    <ListItem key={doc.id}>
                      <TextField
                        label={`Document ${index + 1}`}
                        value={doc.name}
                        onChange={(e) =>
                          updateDocument(doc.id, "name", e.target.value)
                        }
                        fullWidth
                        margin="dense"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={doc.required}
                            onChange={(e) =>
                              updateDocument(
                                doc.id,
                                "required",
                                e.target.checked,
                              )
                            }
                            color="primary"
                          />
                        }
                        label="Required"
                        sx={{ marginLeft: 2 }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => removeDocument(doc.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addDocument}
                  sx={{ marginTop: 1 }}
                >
                  Add Document
                </Button>
              </AccordionDetails>
            </Accordion>

            {/* Save Form Button */}
            <Button
              variant="contained"
              color="primary"
              onClick={saveForm}
              fullWidth
              sx={{
                marginTop: 4,
                color: "black",
                backgroundColor: "#ffd902",
                borderRadius: "40px",
              }}
            >
              Save Form
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </DragDropContext>
  );
}

export default FormEditor;
